import React, { useState, useEffect } from 'react';
// Uses DOMPurify to sanitize HTML (important for security)
import DOMPurify from 'dompurify';
// Uses marked library to convert markdown to HTML
import { marked } from 'marked';

// Configure marked to handle breaks
marked.setOptions({
    async: false,
    breaks: true,  // This will make marked treat single newlines as <br>
    gfm: true      // GitHub Flavored Markdown - includes other useful defaults
});

interface MarkdownPreviewProps {
    content: string;
}

const MarkdownPreview: React.FC<MarkdownPreviewProps> = ({ content }) => {
    const [html, setHtml] = useState<string>('');

    useEffect(() => {
        // Convert markdown to HTML
        const rawMarkdown = marked.parse(content);
        // Sanitize the HTML to prevent XSS attacks
        const sanitizedHtml = DOMPurify.sanitize(rawMarkdown as string);
        setHtml(sanitizedHtml);
    }, [content]);

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold mb-0">Preview</label>
                <small className="text-secondary">Markdown formatting is supported</small>
            </div>
            <div className="border rounded p-3 bg-light">
                <div
                    className="markdown-preview"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            </div>
        </div>
    );
};

// Add some basic styles for the markdown preview
const styles = `
    .markdown-preview h1 { font-size: 2rem; margin-bottom: 1rem; }
    .markdown-preview h2 { font-size: 1.75rem; margin-bottom: 0.875rem; }
    .markdown-preview h3 { font-size: 1.5rem; margin-bottom: 0.75rem; }
    .markdown-preview p { margin-bottom: 1rem; }
    .markdown-preview ul, .markdown-preview ol { margin-bottom: 1rem; padding-left: 2rem; }
    .markdown-preview ul { list-style-type: disc; }
    .markdown-preview ol { list-style-type: decimal; }
    .markdown-preview blockquote {
        border-left: 4px solid #dee2e6;
        margin-left: 0;
        margin-right: 0;
        padding-left: 1rem;
        color: #6c757d;
    }
    .markdown-preview code {
        background-color: #f8f9fa;
        padding: 0.2rem 0.4rem;
        border-radius: 0.25rem;
        font-family: monospace;
    }
    .markdown-preview pre code {
        display: block;
        padding: 1rem;
        margin-bottom: 1rem;
        overflow-x: auto;
    }
    .markdown-preview a {
        color: #0d6efd;
        text-decoration: none;
    }
    .markdown-preview a:hover {
        text-decoration: underline;
    }
    .markdown-preview img {
        max-width: 100%;
        height: auto;
    }
    .markdown-preview br {
        display: block;
        margin: 0.5em 0;
    }
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default MarkdownPreview;